/** @format */
import { ImageWithFallback } from '@mpb-com/feather/components/atoms/image-with-fallback';
import {
    Skeleton,
    SkeletonVariant,
} from '@mpb-com/feather/components/atoms/skeleton';
import { TFunction } from 'i18next';
import { random } from 'lodash';
import React from 'react';

import { KeyFeature, Model, ModelImage } from '@toucan/client/global/model';
import { fallbackImagePath } from '@toucan/client/global/product';

import { ModelOverviewProps } from '@toucan/client/features/model/components/model-overview';

/**
 * Converts model data into props for the ModelOverview component. If
 * the data is not present default values are returned instead
 *
 * @param t The translation function
 * @param model The model to convert data for
 * @param {boolean} isNewModel If the model being displayed is new
 *
 * @returns {ModelOverviewProps|boolean} Returns either the props required for the component
 * or false if the component should not be rendered
 */
export function modelToModelOverviewProps(
    t: TFunction,
    model?: Model | null,
    isNewModel: boolean = false
): ModelOverviewProps | false {
    /**
     * Converts model image data into an image that can be rendered
     */
    function convertImage(imageUrl: string, index: number) {
        return (
            <ImageWithFallback
                src={imageUrl}
                alt={model?.name}
                key={index}
                fallbackSrc={fallbackImagePath}
            />
        );
    }

    /**
     * Formats the key features into a string
     *
     * @param modelFeatures An array of model fea
     */
    function formatKeyFeatures(modelFeatures: KeyFeature[]) {
        // If there aren't any key features, return nothing
        if (modelFeatures.length === 0) {
            return null;
        }

        const keyFeatures = modelFeatures.map(({ name }) => name);
        const lastKeyFeature = keyFeatures.pop();

        const rawKeyFeatures = t('{{keyFeatures}} and {{lastKeyFeature}}', {
            keyFeatures: keyFeatures.join(', '),
            lastKeyFeature,
            context: 'modelOverview',
        });

        return rawKeyFeatures.replace(
            /\s([A-Z][a-z])/g,
            (match, capture: string) => {
                return ` ${capture.toLowerCase()}`;
            }
        );
    }

    if (model) {
        // New model images are shown elsewhere
        const images = isNewModel
            ? []
            : model.images.map((image: ModelImage, index: number) =>
                  convertImage(image.imageUrl, index)
              );

        // Used models should show at least one image
        if (!isNewModel && images.length <= 0) {
            return false;
        }

        // All model overviews should at minimum have a description
        if (!model.description) {
            return false;
        }

        const keyFeatures = formatKeyFeatures(model.keyFeatures);
        const specs = model.modelSpecs
            .filter(({ displayValue }) => displayValue !== '')
            .map(({ type, name, displayValue, value }) => {
                let description;
                if (type === 'BOOL') {
                    description = value === 'False' ? t('No') : t('Yes');
                } else {
                    description = displayValue;
                }
                return {
                    term: name,
                    description,
                };
            });

        return {
            specs,
            images,
            keyFeatures,
            title: model.featuredText ?? model.legend,
            description: model.description,
            modelUrlSegment: model.urlSegment,
            modelName: model.name,
        };
    }

    return {
        title: <Skeleton variant={SkeletonVariant.TEXT} lineCount={2} />,
        description: <Skeleton variant={SkeletonVariant.TEXT} lineCount={3} />,
        keyFeatures: <Skeleton variant={SkeletonVariant.TEXT} />,
        modelUrlSegment: '',
        modelName: '',
        specs: new Array(15).fill(null).map(() => {
            const termWidth = random(0.5, 1);
            const descWidth = random(0.3, 1);
            return {
                term: (
                    <Skeleton
                        variant={SkeletonVariant.TEXT}
                        className="skeleton-wrapper-term"
                        width={`${termWidth * 100}%`}
                    />
                ),
                description: (
                    <Skeleton
                        variant={SkeletonVariant.TEXT}
                        className="skeleton-wrapper-desc"
                        width={`${descWidth * 100}%`}
                    />
                ),
            };
        }),
    };
}
